// Words for the onboarding contract's values (apiv2/onboarding.openapi.yaml). onboardingContract.test.js
// checks that every enum value and refusal code of the contract has words here; an unknown value (a
// newer backend) is shown as it is.

export const PHASES: Record<string, string> = {
    setup: 'Setting up',
    choose: 'Choose tours',
    importing: 'Importing',
    ready: 'Ready',
};

/** A lane's state in words, beside its icon (the summary says more when there is one). */
export const LANE_STATES: Record<string, string> = {
    pending: 'Waiting',
    running: 'In progress',
    done: 'Done',
    attention: 'Needs attention',
    error: 'Our team is looking at this',
    skipped: 'Not needed',
};

export const PRODUCT_STATES: Record<string, string> = {
    queued: 'Waiting to be checked',
    probing: 'Checking bookings…',
    done: 'Checked',
    no_bookings: 'No upcoming bookings',
    error: "Couldn't check this product",
};

export const GO_LIVE_STATUSES: Record<string, string> = {
    none: 'Not requested yet',
    requested: 'Go-live requested',
    approved: 'Approved — going live shortly',
    running: 'Going live…',
    live: "You're live",
    rejected: 'Not approved yet',
};

export const LOGO_STATUSES: Record<string, string> = {
    no_task: 'Upload your logo.',
    running: 'Looking for your logo on your website…',
    failed: "We couldn't search your website for a logo. Upload your logo instead.",
    empty: 'We found no usable logo on your website. Upload your logo.',
    auto: 'We use the logo we found on your website.',
    needs_choice: 'Choose one of the logos we found on your website, or upload your logo.',
    chosen: 'You chose this logo from your website.',
    declined: 'No logo from your website is used.',
};

export const CADENCES: Record<string, string> = {
    day: 'Day tour',
    multi_day: 'Multi-day',
};

/** The page's own words for each refusal code; `detail` (the server's words) is used for anything
 *  else. Codes the page words by context (discovery_running on an import) are worded there. */
export const ERROR_CODES: Record<string, string> = {
    permission_denied: 'Only an account admin can change the onboarding.',
    not_easy: 'Onboarding is not available for this account.',
    onboarding_complete: 'Your account is already live, so the onboarding cannot change any more.',
    rate_limited: "You've checked products many times today. Try again later.",
    no_candidate: 'That logo is no longer available.',
    bad_candidate: 'That logo is no longer available. Choose another one, or upload your logo.',
    image_type: 'Upload a JPG, PNG, GIF or WebP image.',
    image_too_large: 'That image is too large. Upload one under 15 MB and 50 megapixels.',
    image_unreadable: "We couldn't read that image. Upload another file.",
    image_orientation: 'That picture relies on a rotation setting. Rotate it in an image editor, save it and upload it again.',
    image_empty: 'That file is empty. Upload another file.',
    candidate_expired: 'That logo is no longer stored. Upload your logo instead.',
    candidate_unavailable: "The logos can't be read just now. Try again in a moment.",
    upload_too_large: 'That file is too large to upload. Upload one under 15 MB.',
    discovery_running: "We're already checking your products — the list fills in as they're checked.",
    discovery_blocked: "Checking your products waits for a setup step that didn't finish. Our team is looking at it.",
    no_more_products: 'Every product has been checked.',
    invalid_selection: 'One of the ticked tours can no longer be imported. Check the list and try again.',
    invalid_range: "Check the dates: the first can be today or later, and the last can't be before the first.",
    import_running: 'Your earlier choice is still importing. Wait for it to finish.',
    already_ready: 'Your tours are already imported.',
    setup_run_disabled: "Tours can't be imported in this environment.",
    not_ready: 'You can ask to go live once your account is ready: choose your tours, or finish without them, first.',
    go_live_in_progress: 'Our team approved your request and is taking your account live.',
    no_open_request: 'There is no request waiting for our team any more.',
    request_conflict: 'Your request crossed another one sent at the same moment; this is where it stands now.',
};

/** A date-range import's state (OnboardingBookingSync.state): waiting for the tours, then its booking sync's. */
export const BOOKING_SYNC_STATES: Record<string, string> = {
    waiting: 'Waiting for the tours',
    running: 'Importing',
    done: 'Done',
    failed: "Didn't finish",
    cancelled: 'Cancelled',
};

/** The words for a refusal: the page's own for a known code, else the server's `detail`. */
export const errorWords = (code: string | null | undefined, detail: string | null | undefined, fallback = 'That did not go through. Try again.') =>
    (code && ERROR_CODES[code]) || detail || fallback;

/** The sandbox asked for at sign-up (OnboardingSandbox.status): its set-up steps, in order, then `failed`. */
export const SANDBOX_STATUSES: Record<string, string> = {
    requested: 'Requested',
    creating: 'Creating',
    syncing: 'Syncing tour and bookings',
    ready: 'Ready',
    failed: 'Failed',
};

export const SANDBOX_FAILED_WORDS = "We couldn't set up your sandbox. Our team has been told and will look at it.";

/** Why the sandbox set-up failed: the backend's words (OnboardingSandbox.error), else the page's own. */
export const sandboxErrorWords = (error: string | null | undefined) => error || SANDBOX_FAILED_WORDS;

/** The setup gate's steps (OnboardingSetupStatus.steps[].key: the setup checklist's access lane -- employee roles, the
 *  access groups, which the admin joins, and their enforcement); their states read as the lanes' (LANE_STATES). */
export const SETUP_STEPS: Record<string, string> = {
    account: 'Team roles',
    access: 'Access groups',
    admin: 'Access control',
};
